import { useTranslation } from "react-i18next";
import { PATHS } from "@/routes/paths";
import { Spinner } from "@/components/ui/v2/spinner";
import { formatDate } from "date-fns";
import { useGetAttendanceExceptionsQuery } from "@/services/(my-space)/api.time-attendance";
import { ENUM_ATTENDANCE_EXCEPTION_STATUS } from "@/utils/enums/time-attendance.enum";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";

import type { ISingleAttendanceException } from "@/types/(my-space)/timeAttendace.interface";
import { ClockAlert, DoorOpen } from "lucide-react";
import { DashboardCard } from "@/components/common/DashboardCard";

const PRIMARY = "100,56,255";

export const AttendanceExceptionsWidget = () => {
  const { t } = useTranslation("my-space");

  const { data: { data: attendanceExceptions } = {}, isLoading: isFetchingExceptions } =
    useGetAttendanceExceptionsQuery({ status: ENUM_ATTENDANCE_EXCEPTION_STATUS.PENDING });
  const mappedExceptions = attendanceExceptions?.slice(0, 5) ?? [];

  return (
    <DashboardCard
      icon="/icons/interface-icons/stats/time-attendance/icon-3d-calendar-red-x.png"
      title={t("dashboard.attendance_exceptions")}
      action={{
        label: t("ui.view_all_x"),
        link: PATHS.MY_SPACE.MS_TIME_ATTENDANCE_PATHS.ROOT,
      }}
    >
      {isFetchingExceptions ? (
        <Spinner />
      ) : mappedExceptions.length === 0 ? (
        <div
          className="h-full rounded-xl border flex flex-col items-center justify-center gap-3"
          style={{
            background: `linear-gradient(150deg, rgba(${PRIMARY},0.05) 0%, rgba(${PRIMARY},0.10) 100%)`,
            borderColor: `rgba(${PRIMARY},0.20)`,
          }}
        >
          <div className="rounded-full p-4" style={{ background: `rgba(${PRIMARY},0.08)` }}>
            <ClockAlert className="w-7 h-7 text-primary opacity-50" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-500">{t("labels.no_attendance_exceptions")}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t("labels.pending_exceptions_will_appear_here")}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {mappedExceptions.map((exception) => (
            <AttendanceExceptionStrip key={exception.id} exception={exception} />
          ))}
        </div>
      )}
    </DashboardCard>
  );
};

const buildStatusLabels = (t: (key: string) => string): Record<ENUM_ATTENDANCE_EXCEPTION_STATUS, string> => ({
  [ENUM_ATTENDANCE_EXCEPTION_STATUS.PENDING]: t("ui.pending"),
  [ENUM_ATTENDANCE_EXCEPTION_STATUS.APPROVED]: t("labels.approved"),
  [ENUM_ATTENDANCE_EXCEPTION_STATUS.AUTO_APPROVED]: t("ui.auto_approved"),
  [ENUM_ATTENDANCE_EXCEPTION_STATUS.REJECTED]: t("labels.rejected"),
  [ENUM_ATTENDANCE_EXCEPTION_STATUS.CANCELLED]: t("ui.cancelled"),
});

const AttendanceExceptionStrip = ({ exception }: { exception: ISingleAttendanceException }) => {
  const { t } = useTranslation("my-space");
  const label = buildStatusLabels(t)[exception.status] ?? exception.status;

  return (
    <div className="flex items-center gap-3 p-2 rounded-sxs border">
      {/* Icon */}
      <Avatar>
        <AvatarFallback className="bg-blue-200">
          <DoorOpen className="text-info" />
        </AvatarFallback>
      </Avatar>

      {/* Title & Description */}
      <div className="flex flex-col flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{exception.requestReason ?? t("toasts.untitled")}</p>
        <p className="text-xs text-gray-400 truncate">{formatDate(exception.requestDate, "dd MMM, yyyy")}</p>
      </div>

      {/* Status Pill */}
      <Badge variant={statusToVariant(exception.status)} dot={false} className="whitespace-nowrap">
        {label}
      </Badge>
    </div>
  );
};
